import React, { useEffect, useState, useRef } from "react";
import { Button, Theme, makeStyles } from "@material-ui/core";
import Xarrow from "react-xarrows";
import clsx from "clsx";
import { createStyles } from '@material-ui/core/styles';

import JsPDF from 'jspdf';
import ReactDOMServer from "react-dom/server";
import html2canvas from "html2canvas";

const useStyles = makeStyles(() => ({
    root: {
        display: "flex",
        flexDirection: "row",
        margin: 50,
    },
    legend: {
        display: "flex",
        flexDirection: "column",
    },
    singleRow: {
        display: "flex",
        flexDirection: "column",
    },
    cell: {
        width: "70px",
        height: "40px",
        display: "flex",
        border: "1px solid #000",
        justifyContent: "center",
        alignItems: "center",
        fontWeight: 600,
    },
    redBg: {
        backgroundColor: "#d56c6a",
        color: "white",
    },
    cellWrapper: {
        cursor: "pointer",
        width: "70px",
        height: "40px",
        border: "1px solid #000",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    dot: {
        width: 15,
        height: 15,
        borderRadius: "50%",
        backgroundColor: "#000",
    },
    textValueWrapper: {
        border: "1px solid #000",
    },
    textValue: {
        color: "#000",
        padding: "5px 19px",
    },
    inputField: {
        width: "37px",
        background: "#ddd",
    },
    yellowBoxText: {
        background: "#0049a3", padding: "6px 16px", fontSize: 14, fontWeight: 700, color: "#FFF", borderRadius: 4, cursor: "pointer",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "flex",
        justifyContent: "center"
    },
    buttons: {
        display: "block",
        marginTop: 20,
        marginLeft: 40,
        padding: "10px"
    }
}));

const COLUMNS = [
    {
        id: 1,
        text: "1",
    },
    {
        id: 2,
        text: "2",
    },
    {
        id: 3,
        text: "3",
    },
    {
        id: 4,
        text: "4",
    },
    {
        id: 5,
        text: "5",
    },
    {
        id: 6,
        text: "6",
    },
];

const ROWS = [
    {
        id: "r1",
        text: ">34",
        red: true,
    },
    {
        id: "r2",
        text: "30-34",
        red: true,
    },
    {
        id: "r3",
        text: "20-24",
    },
    {
        id: "r4",
        text: "15-19",
    },
    {
        id: "r5",
        text: "10-14",
    },
];

const LinesLayer = ({ refs }: any) => {
    const notNullRefs = refs.filter((r: any) => r !== null);

    const lines: any = [];

    notNullRefs.forEach((r: any, i: any) => {
        if (i !== 0) {
            lines.push(
                <Xarrow key={i} start={notNullRefs[i - 1]} end={r} lineColor={"#000"} showHead={false} showXarrow={true} path={"smooth"} curveness={0.25} />
            );
        }
    });

    return <div>{lines}</div>;
};

const ClickableCell = ({ selected, setSelected, setSelectedRef, isRed, cellTextValue, updateTextValue }: any) => {
    const classes = useStyles();
    const itemRef = useRef(null);

    const [showInputField, setShowInputField] = useState(false);
    const [inputValue, setInputValue] = useState("");

    useEffect(() => {
        if (selected) {
            setSelectedRef(itemRef);
        }
    }, []);

    const clickHandler = () => {
        if (isRed && !selected) {
            setShowInputField(true);
        } else {
            setSelected(itemRef);
        }
    };

    const handleSaveInput = () => {
        if (inputValue !== "") {
            updateTextValue(inputValue);
            setInputValue("");
            setSelected(itemRef);
        }
        setShowInputField(false);
    };

    return (
        <div
            className={clsx(classes.cellWrapper, {
                [classes.redBg]: isRed,
            })}
            onClick={clickHandler}
        >
            {selected && !isRed && !showInputField && <div className={classes.dot} ref={itemRef}></div>}
            {selected && isRed && !showInputField && (
                <div className={classes.textValueWrapper} ref={itemRef}>
                    <span className={classes.textValue}>{cellTextValue}</span>
                </div>
            )}
            {showInputField && (
                <input
                    type="text"
                    autoFocus
                    value={inputValue}
                    className={classes.inputField}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={handleSaveInput}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            handleSaveInput();
                        }
                    }}
                />
            )}
        </div>
    );
};

// Create Document Component
function BasicDocument(): JSX.Element {
    const classes = useStyles();
    const [selectedCellsIndexes, setSelectedCellsIndexes] = useState([-1, -1, 1, -1, 0, 2]);
    const [selectedCellsRefs, setSelectedCellsRefs] = useState([null, null, null, null, null, null]);
    const [cellNumberValues, setCellNumberValues] = useState([null, null, "32", null, "38", null]);

    const selectCell = (columnIndex: number, rowIndex: number, newRef: any) => {
        const newArr = [...selectedCellsIndexes];
        const newRefsArr = [...selectedCellsRefs];
        if (newArr[columnIndex] === rowIndex) {
            newArr[columnIndex] = -1;
            newRefsArr[columnIndex] = null;
        } else {
            newArr[columnIndex] = rowIndex;
            newRefsArr[columnIndex] = newRef;
        }
        setSelectedCellsIndexes(newArr);
        setSelectedCellsRefs(newRefsArr);
    };

    const selectCellRef = (columnIndex: number, newRef: any) => {
        setSelectedCellsRefs((prev) => {
            const newRefsArr = [...prev];
            newRefsArr[columnIndex] = newRef;
            return newRefsArr;
        });
    };

    const YellowBox = ({ title, childComp = null, onClick = null, ...rest }: any) => {
        const classes = useStyles();
        if (!title) return null;
        return (
            <span onClick={onClick} className={classes.yellowBoxText} {...rest}>
                {childComp || title}
            </span>
        )
    }

    function SubHeader() {
        return (
            <div className={classes.buttons}>
                <div style={{ justifyContent: "left", display: "block", float: "left" }}>
                    <YellowBox
                        title="Export pdf"
                        onClick={() => generatePDF()}
                        style={{ marginRight: 10, minWidth: 64 }}
                    />
                </div>
                <div style={{ justifyContent: "left", display: "block", float: "left" }}>
                    <YellowBox
                        title="Export pdf (image)"
                        onClick={() => exportImagePDF()}
                        style={{ marginRight: 10, minWidth: 64 }}
                    />
                </div>
            </div>
        );
    }

    const generatePDF = () => {
        //const doc = new JsPDF('portrait', 'pt', 'a4');
        const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        try {
            const htmlData = document.querySelector<HTMLElement>('#report')!;
            if (htmlData !== null) {
                //console.log(htmlStr);
                doc.html(htmlData).then(() => {
                    doc.save('test.pdf');
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const generateImagePDF = () => {
        const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        //window.html2canvas = html2canvas;
        try {
            const htmlData = document.querySelector<HTMLElement>('#report')!;
            if (htmlData !== null) {
                doc.setFontSize(12);
                doc.html(htmlData, {
                    callback: function (doc) {
                        doc.save('test.pdf');
                    }
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const exportImagePDF = () => {
        const doc = new JsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
        try {
            const htmlData = document.querySelector<HTMLElement>('#report')!;
            if (htmlData !== null) {
                html2canvas(htmlData)
                    .then((canvas) => {
                        const imgData = canvas.toDataURL('image/png');

                        var width = 840;//doc.internal.pageSize.getWidth();
                        var height = 120;//doc.internal.pageSize.getHeight();

                        doc.addImage(imgData, 'png', 100, 100, width, height);
                        doc.save("test.pdf");
                    });
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div>
            <SubHeader />
            <div id="report" className={classes.root}>
                <div className={classes.legend}>
                    {ROWS.map((r) => {
                        return (
                            <div
                                className={clsx(classes.cell, {
                                    [classes.redBg]: r.red,
                                })}
                                key={r.id}
                            >
                                {r.text}
                            </div>
                        );
                    })}
                </div>
                {COLUMNS.map((c, columnIndex) => {
                    return (
                        <div className={classes.singleRow} key={c.id}>
                            {ROWS.map((r, rowIndex) => {
                                return (
                                    <React.Fragment key={`${r.id}${c.id}`}>
                                        <ClickableCell
                                            selected={selectedCellsIndexes[columnIndex] === rowIndex}
                                            setSelected={(newRef: any) => {
                                                selectCell(columnIndex, rowIndex, newRef);
                                            }}
                                            setSelectedRef={(newRef: any) => {
                                                selectCellRef(columnIndex, newRef);
                                            }}
                                            cellTextValue={selectedCellsIndexes[columnIndex] === rowIndex && cellNumberValues[columnIndex]}
                                            updateTextValue={(newVal: string) => {
                                                setCellNumberValues((prev) => {
                                                    const newRefsArr = [...prev];
                                                    newRefsArr[columnIndex] = newVal;
                                                    return newRefsArr;
                                                });
                                            }}
                                            isRed={r.red}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    );
                })}

                <LinesLayer refs={selectedCellsRefs} />
            </div>
        </div>
    );
}
export default BasicDocument;